import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';

/**
 * Standard page shell for every list/table screen so they all look identical
 * (the "Selected Candidates" card style). Renders:
 *  - an outer page gutter
 *  - a rounded glass Paper card (theme MuiPaper.elevation0 = radius 20 + soft shadow)
 *  - a consistent header: bold h5 title (+ optional count chip) on the left,
 *    an `actions` slot (search / filters / buttons) on the right.
 *
 * Put the table (TableWrap/MUI Table) + pagination as children.
 *
 * Props:
 *  - title    : string heading (rendered as h5, fontWeight 700)
 *  - count    : optional number shown as a primary chip next to the title
 *  - actions  : optional node(s) aligned to the right of the header
 *  - children : the table + pagination
 *  - sx       : extra sx for the outer gutter Box
 *  - cardSx   : extra sx for the inner Paper card
 */
export default function TableCard({ title, count, actions, children, sx, cardSx }) {
  const showHeader = title || actions || count != null;
  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, ...sx }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, ...cardSx }}>
        {showHeader && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {title && (
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
              )}
              {count != null && (
                <Chip
                  label={count}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 700, borderRadius: '8px' }}
                />
              )}
            </Box>
            {actions && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {actions}
              </Box>
            )}
          </Box>
        )}
        {children}
      </Paper>
    </Box>
  );
}
